/**
 * Client-side thumbnail URL helpers.
 *
 * Centralises the three URL flavours so VideoRow / FtsVideoCard /
 * ReuploadDiscoveryPage share one source of truth for fallbacks.
 *
 * Why no `video.thumbnail_url` fallback for DELETED videos? YT serves
 * its signed-URL thumbnails (`?sqp=…&rs=…`) as **HTTP 404 + 1097-byte
 * grey JPEG body**. Browsers happily render that body (a 120×90 grey
 * placeholder with a play-icon glyph) because the response carries
 * `Content-Type: image/jpeg`. So defaulting to that URL always shows
 * a grey image instead of the user's "no thumb" placeholder.
 *
 * Wayback's `web/0im_/...` gateway 302s to the closest snapshot of the
 * canonical (unsigned) YT URL. ~22-25 % of deleted videos have a
 * usable Wayback snapshot (bench: thumbnail-hybrid-detection memo).
 * The remaining hits 404 → onError → "No thumb" placeholder.
 */

/**
 * Server-side video-file redirect endpoint. Point "Watch on archive.org" /
 * download links here instead of straight at `video.videoFileUrl` (the fragile
 * fakeurl pointer): the endpoint 302s to a working source and, during a fakeurl
 * gateway outage, resolves + heals the capture via `__wb/videoinfo` so the link
 * keeps working. See server `videoFileResolver.ts` (Phase 2, wayback-outage system).
 */
export function videoFileHref(videoId: string): string {
  return `/api/videos/${videoId}/file`;
}

/** Local cached thumbnail path (mirrors server-side `mediaStorage.thumbnailUrlPath`). */
export function localThumbnailUrl(videoId: string): string {
  return `/media/thumbnails/${videoId.slice(0, 2)}/${videoId.slice(2, 4)}/${videoId}.jpg`;
}

/**
 * On-demand thumbnail endpoint: 302s to the locally-cached file, downloading
 * it into the media gallery first (YT → Wayback cascade, server-side) when we
 * don't have it yet. Use ONLY on single-video surfaces (find-reupload) — each
 * cache-miss costs the server an outbound YT/Wayback fetch, so list views
 * (VideoRow / FtsVideoCard) must keep using `pickVideoThumbnail` instead.
 */
export function onDemandThumbnailUrl(videoId: string): string {
  return `/api/videos/${videoId}/thumbnail`;
}

/**
 * Wayback's redirect gateway pointing at the canonical (unsigned) YT
 * thumbnail. The browser follows 302 → snapshot. For DELETED videos
 * with no `thumbnail_archive_url` stored, this is the only realistic
 * way to surface a real frame from the archive.
 */
export function waybackHqdefaultUrl(videoId: string): string {
  return `https://web.archive.org/web/0im_/https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
}

/**
 * URL path for a Yandex preview MP4 we downloaded. Mirrors the server's
 * `mediaStorage.yandexPreviewUrlPath` — sharded by `internalId[0..1]/[2..3]`
 * so no single directory grows unbounded.
 */
export function yandexPreviewPath(yandexInternalId: string): string {
  return `/media/yandex_previews/${yandexInternalId.slice(0, 2)}/${yandexInternalId.slice(2, 4)}/${yandexInternalId}.mp4`;
}

/** What Yandex holds for this video, if anything. */
export type YandexFrame =
  /** We downloaded the animated preview; `stillUrl` backs it if the MP4 fails. */
  | { kind: 'mp4'; mp4Url: string; stillUrl: string | null }
  /** No local MP4, but Yandex published a still frame. */
  | { kind: 'still'; stillUrl: string };

/**
 * The two INDEPENDENT pictures a video can have, kept apart on purpose.
 *
 * These are different things and used to be collapsed into one slot, with the
 * Yandex preview simply winning: the archived cover was demoted to a
 * third-tier error fallback and in practice never rendered. That hid a real
 * gap — 62,957 rows matched in Yandex, 61,132 of them carry a Yandex frame,
 * and only 5,438 (8.6%) have a local thumbnail, so a row could look perfect in
 * the list while its `thumbnail_status` sat at `not_found` forever. The
 * find-reupload page, which reads only the thumbnail columns, then said "No
 * thumb" about 7,899 videos of which 7,874 had a Yandex frame all along.
 *
 * So: `original` is the video's OWN cover (our copy, the live YT URL, or an
 * archived capture) and `yandex` is a frame Yandex indexed. Surfaces render
 * whichever they have, side by side when both, and reverse-image search offers
 * a button per source.
 *
 * `original` falls back to Wayback's blind `0im_` gateway ONLY when there is no
 * Yandex frame. The gateway misses far more often than it hits, and an empty
 * box next to a good picture reads as breakage; alone it is still the best
 * remaining guess.
 */
export function pickThumbnailSources(input: {
  videoId: string;
  status: string | null;
  thumbnailStatus: string | null;
  thumbnailUrl: string | null;
  thumbnailArchiveUrl: string | null;
  yandexInternalId?: string | null;
  yandexPreviewLocal?: boolean | null;
  yandexThumbUrl?: string | null;
}): { original: string | null; yandex: YandexFrame | null } {
  const yandex: YandexFrame | null =
    input.yandexInternalId && input.yandexPreviewLocal === true
      ? { kind: 'mp4', mp4Url: yandexPreviewPath(input.yandexInternalId), stillUrl: input.yandexThumbUrl ?? null }
      : input.yandexThumbUrl
        ? { kind: 'still', stillUrl: input.yandexThumbUrl }
        : null;

  const hasLocalThumb =
    input.thumbnailStatus === 'available_youtube' || input.thumbnailStatus === 'available_archive';
  let original: string | null;
  if (hasLocalThumb) {
    original = localThumbnailUrl(input.videoId);
  } else if (input.status === 'LIVE') {
    original = input.thumbnailUrl ?? `https://i.ytimg.com/vi/${input.videoId}/mqdefault.jpg`;
  } else if (input.thumbnailArchiveUrl) {
    original = input.thumbnailArchiveUrl;
  } else {
    original = yandex ? null : waybackHqdefaultUrl(input.videoId);
  }

  return { original, yandex };
}

/**
 * Pick the best thumbnail src for a video row. Mirrors the logic
 * previously inlined in VideoRow / FtsVideoCard / ReuploadDiscoveryPage.
 *
 *   1. If we already downloaded the thumbnail (`available_youtube` or
 *      `available_archive`), serve the local cached file.
 *   2. For LIVE rows: trust `thumbnail_url` (signed YT URLs ARE valid
 *      for live videos; the grey-fallback issue only affects DELETED).
 *   3. For non-LIVE rows: prefer the stored Wayback URL (it points at a
 *      known-good snapshot), otherwise hit Wayback's `web/0im_/` gateway
 *      directly. NEVER fall back to `thumbnail_url` for non-LIVE — YT's
 *      signed URLs render as a grey placeholder for deleted videos.
 */
export function pickVideoThumbnail(input: {
  videoId: string;
  status: string | null;
  thumbnailStatus: string | null;
  thumbnailUrl: string | null;
  thumbnailArchiveUrl: string | null;
}): string | null {
  const hasLocalThumb =
    input.thumbnailStatus === 'available_youtube' ||
    input.thumbnailStatus === 'available_archive';
  if (hasLocalThumb) return localThumbnailUrl(input.videoId);
  const isLive = input.status === 'LIVE';
  if (isLive) {
    return input.thumbnailUrl ?? `https://i.ytimg.com/vi/${input.videoId}/mqdefault.jpg`;
  }
  return input.thumbnailArchiveUrl ?? waybackHqdefaultUrl(input.videoId);
}
